import { format } from "date-fns";
import { Loader2, X, Check, Clock, CalendarOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";

const STATUS_CLASS = {
  approved: "bg-forest/10 text-forest border-forest/30",
  pending: "bg-ochre/10 text-ochre border-ochre/30",
  rejected: "bg-destructive/10 text-destructive border-destructive/30",
};

/**
 * Leave Requests tab: filter controls + the list of leave requests.
 */
export function RosterShiftList({
  leaveFilter,
  onLeaveFilterChange,
  leaveCustomStart,
  onLeaveCustomStartChange,
  leaveCustomEnd,
  onLeaveCustomEndChange,
  leaveLoading,
  leaveRequests,
  isAdmin,
  onUpdateStatus,
  onDelete,
}) {
  return (
    <Card className="border-border">
      <CardHeader>
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <CardTitle className="font-heading text-forest">Leave Requests</CardTitle>
          <div className="flex items-center gap-2 flex-wrap">
            <Select value={leaveFilter} onValueChange={onLeaveFilterChange}>
              <SelectTrigger className="w-[140px] h-9" data-testid="leave-filter-preset">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="this_week">This Week</SelectItem>
                <SelectItem value="this_month">This Month</SelectItem>
                <SelectItem value="all">All Requests</SelectItem>
                <SelectItem value="custom">Custom Range</SelectItem>
              </SelectContent>
            </Select>
            {leaveFilter === "custom" && (
              <>
                <Input
                  type="date"
                  value={leaveCustomStart}
                  onChange={(e) => onLeaveCustomStartChange(e.target.value)}
                  className="w-[140px] h-9"
                  data-testid="leave-filter-start"
                />
                <span className="text-sage text-sm">to</span>
                <Input
                  type="date"
                  value={leaveCustomEnd}
                  onChange={(e) => onLeaveCustomEndChange(e.target.value)}
                  className="w-[140px] h-9"
                  data-testid="leave-filter-end"
                />
              </>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {leaveLoading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin text-forest" />
          </div>
        ) : leaveRequests.length === 0 ? (
          <div className="text-center py-8 text-sage">
            <CalendarOff className="h-12 w-12 mx-auto mb-3 opacity-50" />
            <p>No leave requests for this period</p>
          </div>
        ) : (
          <div className="space-y-3">
            {leaveRequests.map((lr) => (
              <div
                key={lr.id}
                className="flex items-center justify-between p-4 bg-sand rounded-xl"
                data-testid={`leave-request-${lr.id}`}
              >
                <div className="flex-1">
                  <div className="flex items-center gap-3">
                    <p className="font-medium text-forest">{lr.staff_name}</p>
                    <Badge variant="outline" className={STATUS_CLASS[lr.status] || ""}>
                      {lr.status}
                    </Badge>
                  </div>
                  <p className="text-sm text-sage mt-1">
                    <Clock className="h-3 w-3 inline mr-1" />
                    {(() => {
                      const s = lr.start_date || lr.date;
                      const e = lr.end_date || lr.date;
                      return s === e
                        ? format(new Date(s), "EEEE, MMMM d, yyyy")
                        : `${format(new Date(s), "MMM d")} – ${format(new Date(e), "MMM d, yyyy")}`;
                    })()}
                  </p>
                  <p className="text-sm text-forest/70 mt-1">{lr.reason}</p>
                </div>

                {lr.status === "pending" && isAdmin && (
                  <div className="flex items-center gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-forest border-forest hover:bg-forest hover:text-white"
                      onClick={() => onUpdateStatus(lr.id, "approved")}
                      data-testid={`approve-leave-${lr.id}`}
                    >
                      <Check className="h-4 w-4 mr-1" />
                      Approve
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-destructive border-destructive hover:bg-destructive hover:text-white"
                      onClick={() => onUpdateStatus(lr.id, "rejected")}
                      data-testid={`reject-leave-${lr.id}`}
                    >
                      <X className="h-4 w-4 mr-1" />
                      Reject
                    </Button>
                  </div>
                )}

                {isAdmin && (
                  <Button
                    size="icon"
                    variant="ghost"
                    className="text-sage hover:text-destructive ml-2"
                    onClick={() => onDelete(lr.id)}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                )}
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
